import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ChevronUp, ChevronDown, ChevronRight, Check, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "src/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "src/components/ui/dialog";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { cn } from "src/lib/utils";
import { CommissionTableEmptyState } from "./commission-table-empty-state";
import { Input } from "src/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import BorderedInput from "src/components/form-new/bordered-input";
import BorderedSelect from "src/components/form-new/bordered-select";
import Checkbox from "src/components/form-new/checkbox";
import type {
  ExternalRep,
  ExternalRepPlanType,
  ReferralLine,
  ReferralInvoiceLineItem,
  ReferralLineItemOverride,
  CommissionInvoice,
  CommissionReport,
} from "../types/commissions.types";
import {
  useUpdateCommissionReferralMutation,
  useCreateCommissionReferralLineMutation,
  useUpdateCommissionReferralLineMutation,
  useDeleteCommissionReferralLineMutation,
  useGetCommissionReportsQuery,
  useUpdateCommissionReportMutation,
} from "../endpoints/commissions.endpoints";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { formatCurrency } from "../../utils/billing-utils";

const PLAN_TYPE_OPTIONS: { value: ExternalRepPlanType; label: string }[] = [
  { value: "flat_percent", label: "Flat percent" },
  { value: "initial_and_subsequent", label: "Initial and subsequent" },
  { value: "category_bucketed", label: "Category bucketed" },
  { value: "net_of_overhead_manual", label: "Net of overhead (manual)" },
];

const MONTH_OPTIONS = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function getYearOptions(): { value: string; label: string }[] {
  const current = new Date().getFullYear();
  const options: { value: string; label: string }[] = [];
  for (let y = current - 5; y <= current + 2; y++) {
    options.push({ value: String(y), label: String(y) });
  }
  return options;
}

const YEAR_OPTIONS = getYearOptions();

function Label({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn(portalDs.label, className)}>
      {children}
    </span>
  );
}

export interface InvoiceOption {
  value: string;
  label: string;
}

/** Local line with string rate for input; id used as React key; apiId = API line id when saved */
interface ReferralLineRow {
  id: string;
  apiId?: string;
  companyId: string;
  invoiceId: string;
  rate: string;
  isRecurring: boolean;
  /** When not recurring: month 1–12 (empty string when unset) */
  month: string;
  /** When not recurring: year (empty string when unset) */
  year: string;
}

export interface ExternalRepDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rep: ExternalRep | null;
  isNew: boolean;
  onSave: (rep: Partial<ExternalRep> & { id?: string }) => void;
  /** Options for company select per line (from b2b/billing/companies). */
  companyOptions?: InvoiceOption[];
  /** Map company id (select value) to ext_customer_id for the referral line API. */
  companyIdToExtCustomerId?: Record<string, string>;
  /** Options for invoice select per line (e.g. from commission invoices). */
  invoiceOptions?: InvoiceOption[];
  /** Invoice line items by invoice ID for the Edit line expansion (optional; uses mock items when missing). */
  invoiceLineItemsByInvoiceId?: Record<string, ReferralInvoiceLineItem[]>;
  /** All commission invoices (for expand: show invoices and lines by company). */
  commissionInvoices?: CommissionInvoice[];
}

/** Default mock line items when no invoice line items are provided for an invoice */
function getMockLineItemsForInvoice(_invoiceId: string): ReferralInvoiceLineItem[] {
  return [
    { id: "li-1", description: "Line item 1" },
    { id: "li-2", description: "Line item 2" },
    { id: "li-3", description: "Line item 3" },
  ];
}

const ExternalRepDrawer: React.FC<ExternalRepDrawerProps> = ({
  open,
  onOpenChange,
  rep,
  isNew,
  onSave,
  companyOptions = [],
  companyIdToExtCustomerId = {},
  invoiceOptions = [],
  invoiceLineItemsByInvoiceId = {},
  commissionInvoices = [],
}) => {
  // Single form for create and edit: name, email, referral lines (invoice + rate + recurring per line)
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [planType, setPlanType] = useState<ExternalRepPlanType>("flat_percent");
  const [lines, setLines] = useState<ReferralLineRow[]>([]);
  const [expandedLineId, setExpandedLineId] = useState<string | null>(null);
  /** lineItemOverrides[rowId][lineItemId] = { rate, ignored } */
  const [lineItemOverrides, setLineItemOverrides] = useState<
    Record<string, Record<string, { rate: string; ignored: boolean }>>
  >({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [breakdownExpanded, setBreakdownExpanded] = useState(false);
  /** Editable commission value per report row id (for month-by-month breakdown) */
  const [editingCommissionByReportId, setEditingCommissionByReportId] = useState<Record<string, string>>({});
  /** Filters company dropdown options in referral lines table */
  const [companyMenuSearch, setCompanyMenuSearch] = useState("");
  const initialSnapshot = useRef<{
    name: string;
    email: string;
    planType: ExternalRepPlanType;
    linesJson: string;
    overridesJson: string;
  }>({ name: "", email: "", planType: "flat_percent", linesJson: "", overridesJson: "" });
  const initialLinesRef = useRef<Record<string, { companyId: string; rate: string; isRecurring: boolean; month: string; year: string }>>({});
  const initialLineItemOverridesRef = useRef<Record<string, Record<string, { rate: string; ignored: boolean }>>>({});

  const [updateReferral, { isLoading: isUpdatingReferral }] = useUpdateCommissionReferralMutation();
  const [createLine, { isLoading: isCreatingLine }] = useCreateCommissionReferralLineMutation();
  const [updateLineApi, { isLoading: isUpdatingLine }] = useUpdateCommissionReferralLineMutation();
  const [deleteReferralLine] = useDeleteCommissionReferralLineMutation();
  const [updateCommissionReport] = useUpdateCommissionReportMutation();

  const getLineItemsForInvoice = useCallback(
    (invoiceId: string): ReferralInvoiceLineItem[] => {
      if (!invoiceId.trim()) return [];
      const provided = invoiceLineItemsByInvoiceId[invoiceId];
      if (provided?.length) return provided;
      return getMockLineItemsForInvoice(invoiceId);
    },
    [invoiceLineItemsByInvoiceId]
  );

  const { data: allReferralReports = [] } = useGetCommissionReportsQuery(
    { type: "referral" },
    { skip: !open || !rep?.id || isNew }
  );
  const referralBreakdownRows = useMemo(() => {
    if (!rep?.id) return [];
    const id = rep.id;
    const filtered = allReferralReports.filter(
      (r) => (r.referralId ?? r.referral) === id
    );
    return [...filtered].sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);
  }, [allReferralReports, rep?.id]);

  const filteredCompanyOptions = useMemo(() => {
    const q = companyMenuSearch.trim().toLowerCase();
    if (!q) return companyOptions;
    return companyOptions.filter(
      (opt) =>
        (opt.label ?? "").toLowerCase().includes(q)
        || (opt.value ?? "").toLowerCase().includes(q)
    );
  }, [companyOptions, companyMenuSearch]);

  useEffect(() => {
    if (open) {
      setExpandedLineId(null);
      setShowDiscardConfirm(false);
      setBreakdownExpanded(false);
      setEditingCommissionByReportId({});
      setCompanyMenuSearch("");
      if (rep) {
        const n = rep.name ?? rep.organizationName ?? "";
        const e = rep.email ?? rep.contactEmail ?? "";
        setName(n);
        setEmail(e);
        setPlanType(rep.planType ?? "flat_percent");
        const fromLines = rep.referralLines;
        if (fromLines?.length) {
          const newLines = fromLines.map((ln, i) => {
            const lineId = ln.id ?? `line-${i}`;
            return {
              id: lineId,
              apiId: ln.id,
              companyId: ln.companyId ?? "",
              invoiceId: ln.invoiceId ?? "",
              rate: String(ln.rate ?? ""),
              isRecurring: ln.isRecurring ?? false,
              month: ln.month != null ? String(ln.month) : "",
              year: ln.year != null ? String(ln.year) : "",
            };
          });
          setLines(newLines);
          initialLinesRef.current = newLines.reduce(
            (acc, l) => {
              acc[l.id] = { companyId: l.companyId, rate: l.rate, isRecurring: l.isRecurring, month: l.month, year: l.year };
              if (l.apiId) acc[l.apiId] = acc[l.id];
              return acc;
            },
            {} as Record<string, { companyId: string; rate: string; isRecurring: boolean; month: string; year: string }>
          );
          const overrides: Record<string, Record<string, { rate: string; ignored: boolean }>> = {};
          fromLines.forEach((ln, i) => {
            const rowId = ln.id ?? `line-${i}`;
            overrides[rowId] = {};
            if (ln.lines?.length) {
              ln.lines.forEach((item) => {
                overrides[rowId][item.id] = {
                  rate: String(item.rate),
                  ignored: false,
                };
              });
            }
            if (ln.lineItemOverrides?.length) {
              ln.lineItemOverrides.forEach((o) => {
                overrides[rowId][o.lineItemId] = {
                  rate: String(o.rate),
                  ignored: o.ignored,
                };
              });
            }
          });
          setLineItemOverrides(overrides);
          initialLineItemOverridesRef.current = JSON.parse(JSON.stringify(overrides));
          initialSnapshot.current = {
            name: n,
            email: e,
            planType: rep.planType ?? "flat_percent",
            linesJson: JSON.stringify(newLines.map((l) => ({ companyId: l.companyId, invoiceId: l.invoiceId, rate: l.rate, isRecurring: l.isRecurring, month: l.month, year: l.year }))),
            overridesJson: JSON.stringify(overrides),
          };
        } else if (rep.initialRatePercent != null) {
          const newLines = [
            {
              id: "line-0",
              companyId: "",
              invoiceId: "",
              rate: String(rep.initialRatePercent ?? ""),
              isRecurring: false,
              month: "",
              year: "",
            },
          ];
          setLines(newLines);
          setLineItemOverrides({});
          initialLinesRef.current = {};
          initialLineItemOverridesRef.current = {};
          initialSnapshot.current = {
            name: n,
            email: e,
            planType: rep.planType ?? "flat_percent",
            linesJson: JSON.stringify(newLines.map((l) => ({ companyId: l.companyId, invoiceId: l.invoiceId, rate: l.rate, isRecurring: l.isRecurring, month: l.month, year: l.year }))),
            overridesJson: "{}",
          };
        } else {
          const n = rep.name ?? rep.organizationName ?? "";
          const e = rep.email ?? rep.contactEmail ?? "";
          const newLines = [
            {
              id: "line-0",
              companyId: "",
              invoiceId: "",
              rate: String(rep.initialRatePercent ?? ""),
              isRecurring: false,
              month: "",
              year: "",
            },
          ];
          setLines(newLines);
          setLineItemOverrides({});
          initialLinesRef.current = {};
          initialLineItemOverridesRef.current = {};
          initialSnapshot.current = {
            name: n,
            email: e,
            planType: rep.planType ?? "flat_percent",
            linesJson: JSON.stringify(newLines.map((l) => ({ companyId: l.companyId, invoiceId: l.invoiceId, rate: l.rate, isRecurring: l.isRecurring, month: l.month, year: l.year }))),
            overridesJson: "{}",
          };
        }
      } else {
        setName("");
        setEmail("");
        setPlanType("flat_percent");
        setLines([]);
        setLineItemOverrides({});
        initialLinesRef.current = {};
        initialLineItemOverridesRef.current = {};
        initialSnapshot.current = { name: "", email: "", planType: "flat_percent", linesJson: "[]", overridesJson: "{}" };
      }
    }
  }, [open, rep, isNew]);

  const isDirty = (() => {
    const init = initialSnapshot.current;
    const linesJson = JSON.stringify(lines.map((l) => ({ companyId: l.companyId, invoiceId: l.invoiceId, rate: l.rate, isRecurring: l.isRecurring, month: l.month, year: l.year })));
    const overridesJson = JSON.stringify(lineItemOverrides);
    return name !== init.name || email !== init.email || planType !== init.planType || linesJson !== init.linesJson || overridesJson !== init.overridesJson;
  })();

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isDirty) {
        setShowDiscardConfirm(true);
        return;
      }
      onOpenChange(nextOpen);
    },
    [isDirty, onOpenChange]
  );

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSave = () => {
    const referralLines: ReferralLine[] = lines
      .filter((row) => row.companyId.trim() !== "" || row.invoiceId.trim() !== "")
      .map((row) => {
        const rateNum = parseFloat(row.rate);
        const overrides = lineItemOverrides[row.id];
        const lineItemOverridesPayload: ReferralLineItemOverride[] | undefined =
          overrides && Object.keys(overrides).length > 0
            ? Object.entries(overrides).map(([lineItemId, o]) => ({
                lineItemId,
                rate: Number.isNaN(parseFloat(o.rate)) ? 0 : parseFloat(o.rate),
                ignored: o.ignored,
              }))
            : undefined;
        const monthNum = row.month.trim() ? parseInt(row.month, 10) : undefined;
        const yearNum = row.year.trim() ? parseInt(row.year, 10) : undefined;
        return {
          ...(row.companyId.trim() !== "" && { companyId: row.companyId.trim() }),
          invoiceId: row.invoiceId.trim(),
          rate: Number.isNaN(rateNum) ? 0 : rateNum,
          isRecurring: row.isRecurring,
          ...(!row.isRecurring && monthNum != null && !Number.isNaN(monthNum) && { month: monthNum }),
          ...(!row.isRecurring && yearNum != null && !Number.isNaN(yearNum) && { year: yearNum }),
          id: row.id,
          lineItemOverrides: lineItemOverridesPayload,
        };
      });
    const firstRate = referralLines[0]?.rate ?? 0;
    onSave({
      ...(rep?.id ? { id: rep.id } : {}),
      name: name.trim() || undefined,
      email: email.trim() || undefined,
      planType,
      referralLines: referralLines.length ? referralLines : undefined,
      organizationName: name.trim() || undefined,
      contactEmail: email.trim() || undefined,
      initialRatePercent: firstRate,
      recurringRatePercent: firstRate,
      assignedCustomerIds: referralLines
        .map((l) => l.companyId)
        .filter((id): id is string => id != null && id !== ""),
    });
    onOpenChange(false);
  };

  const addLine = useCallback(() => {
    setLines((prev) => [
      ...prev,
      {
        id: `line-${Date.now()}`,
        companyId: "",
        invoiceId: "",
        rate: "",
        isRecurring: false,
        month: "",
        year: "",
      },
    ]);
  }, []);

  const isRepDirty = !!(rep?.id && (name !== initialSnapshot.current.name || email !== initialSnapshot.current.email));
  const getCustomerName = useCallback((companyId: string) => companyOptions.find((c) => c.value === companyId)?.label ?? "", [companyOptions]);

  const getCompanyName = useCallback(
    (companyId: string) => companyOptions.find((c) => c.value === companyId)?.label ?? "",
    [companyOptions]
  );

  const getInvoicesForCompany = useCallback(
    (companyId: string): CommissionInvoice[] => {
      if (!companyId.trim()) return [];
      const companyName = getCompanyName(companyId);
      return commissionInvoices.filter(
        (inv) =>
          inv.extCustomerId === companyId ||
          (inv.companyName?.toLowerCase() === companyName?.toLowerCase())
      );
    },
    [commissionInvoices, getCompanyName]
  );

  const getLineItemName = useCallback(
    (companyId: string, lineItemId: string): string => {
      const invs = getInvoicesForCompany(companyId);
      for (const inv of invs) {
        const line = inv.lines?.find((l) => l.id === lineItemId);
        if (line) return line.name ?? "";
      }
      return "";
    },
    [getInvoicesForCompany]
  );

  const getInvoiceIdForLineItem = useCallback(
    (companyId: string, lineItemId: string): string => {
      const invs = getInvoicesForCompany(companyId);
      for (const inv of invs) {
        const line = inv.lines?.find((l) => l.id === lineItemId);
        if (line) return inv.id;
      }
      return "";
    },
    [getInvoicesForCompany]
  );

  const handleSaveReferralFields = useCallback(async () => {
    if (!rep?.id || !name.trim()) return;
    try {
      await updateReferral({ id: rep.id, name: name.trim(), email: email.trim() }).unwrap();
      initialSnapshot.current = { ...initialSnapshot.current, name: name.trim(), email: email.trim() };
    } catch {
      // error could be shown via toast
    }
  }, [rep?.id, name, email, updateReferral]);

  const isLineDirty = useCallback(
    (row: ReferralLineRow) => {
      if (!row.apiId) return false;
      const init = initialLinesRef.current[row.apiId] ?? initialLinesRef.current[row.id];
      const baseDirty = init
        ? row.companyId !== init.companyId ||
          row.rate !== init.rate ||
          row.isRecurring !== init.isRecurring ||
          row.month !== init.month ||
          row.year !== init.year
        : false;
      const overridesDirty =
        JSON.stringify(lineItemOverrides[row.id] ?? {}) !==
        JSON.stringify(initialLineItemOverridesRef.current[row.id] ?? {});
      return baseDirty || overridesDirty;
    },
    [lineItemOverrides]
  );

  const canSaveNewLine = useCallback((row: ReferralLineRow) => {
    const base = row.companyId.trim() !== "" && row.rate.trim() !== "" && !Number.isNaN(parseFloat(row.rate));
    if (row.isRecurring) return base;
    const monthOk = row.month.trim() !== "" && !Number.isNaN(parseInt(row.month, 10)) && parseInt(row.month, 10) >= 1 && parseInt(row.month, 10) <= 12;
    const yearOk = row.year.trim() !== "" && !Number.isNaN(parseInt(row.year, 10));
    return base && monthOk && yearOk;
  }, []);

  const handleSaveLine = useCallback(
    async (row: ReferralLineRow) => {
      if (!rep?.id) return;
      const customerName = getCustomerName(row.companyId);
      const rateNum = parseFloat(row.rate);
      const overrides = lineItemOverrides[row.id];
      const linesPayload =
        overrides && Object.keys(overrides).length > 0
          ? Object.entries(overrides).map(([id, o]) => ({
              id,
              name: getLineItemName(row.companyId, id),
              rate: Number.isNaN(parseFloat(o.rate)) ? 0 : parseFloat(o.rate),
              invoiceId: getInvoiceIdForLineItem(row.companyId, id) || undefined,
            }))
          : undefined;
      const monthNum = row.month.trim() ? parseInt(row.month, 10) : undefined;
      const yearNum = row.year.trim() ? parseInt(row.year, 10) : undefined;
      const payload = {
        rate: Number.isNaN(rateNum) ? 0 : rateNum,
        recurring: row.isRecurring,
        customer_name: customerName,
        ext_customer_id: companyIdToExtCustomerId[row.companyId] ?? row.companyId.trim(),
        ...(linesPayload && linesPayload.length > 0 && { lines: linesPayload }),
        ...(!row.isRecurring && monthNum != null && !Number.isNaN(monthNum) && { month: monthNum }),
        ...(!row.isRecurring && yearNum != null && !Number.isNaN(yearNum) && { year: yearNum }),
      };
      try {
        if (row.apiId) {
          await updateLineApi({ id: row.apiId, ...payload }).unwrap();
        } else {
          await createLine({ referral: rep.id, ...payload }).unwrap();
        }
        initialLinesRef.current[row.id] = { companyId: row.companyId, rate: row.rate, isRecurring: row.isRecurring, month: row.month, year: row.year };
        if (row.apiId) initialLinesRef.current[row.apiId] = initialLinesRef.current[row.id];
      } catch {
        // error could be shown via toast
      }
    },
    [rep?.id, getCustomerName, getLineItemName, getInvoiceIdForLineItem, companyIdToExtCustomerId, lineItemOverrides, createLine, updateLineApi]
  );

  const removeLine = useCallback(
    async (id: string) => {
      const row = lines.find((r) => r.id === id);
      if (row?.apiId) {
        try {
          await deleteReferralLine({ id: row.apiId }).unwrap();
        } catch {
          // Error could be shown via toast
        }
      }
      setLines((prev) => prev.filter((r) => r.id !== id));
    },
    [lines, deleteReferralLine]
  );

  const updateLine = useCallback((id: string, patch: Partial<ReferralLineRow>) => {
    setLines((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }, []);

  const getOrInitLineItemOverride = useCallback(
    (rowId: string, lineItemId: string, fallbackRate: string): { rate: string; ignored: boolean } => {
      const rowOverrides = lineItemOverrides[rowId];
      const existing = rowOverrides?.[lineItemId];
      if (existing) return existing;
      return { rate: fallbackRate, ignored: false };
    },
    [lineItemOverrides]
  );

  const updateLineItemOverride = useCallback(
    (rowId: string, lineItemId: string, patch: Partial<{ rate: string; ignored: boolean }>) => {
      setLineItemOverrides((prev) => {
        const rowOverrides = prev[rowId] ?? {};
        const current = rowOverrides[lineItemId] ?? { rate: "", ignored: false };
        const next = { ...rowOverrides, [lineItemId]: { ...current, ...patch } };
        return { ...prev, [rowId]: next };
      });
    },
    []
  );

  const toggleExpanded = useCallback((rowId: string) => {
    setExpandedLineId((prev) => (prev === rowId ? null : rowId));
  }, []);

  const renderPlanTemplateRow = () => (
    <div className="flex gap-4 py-4 items-end">
      <div className="flex-1 min-w-0 max-w-md flex flex-col gap-1.5">
        <label className={portalDs.label}>Plan template</label>
        <BorderedSelect
          label=""
          options={PLAN_TYPE_OPTIONS}
          value={planType}
          onChange={(e) => setPlanType(e.target.value as ExternalRepPlanType)}
        />
      </div>
    </div>
  );

  const renderAssignedCustomers = () => {
    const assignedIds = [
      ...new Set([
        ...(rep?.assignedCustomerIds ?? []),
        ...lines.map((l) => l.companyId).filter((id) => id.trim() !== ""),
      ]),
    ];
    if (assignedIds.length === 0) return null;
    return (
      <div className="mb-4">
        <Label className="block mb-2">Assigned customers</Label>
        <ul className={cn(portalDs.tableContainer, "divide-y divide-[#EDEEF2]")}>
          {assignedIds.map((id) => (
            <li key={id} className={cn(portalDs.tableCell, "px-3 py-2")}>
              {getCompanyName(id) || id}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Top: name/email row; plan template; breakdown; referral lines table
  const renderNameEmailRow = () => (
    <div className="flex gap-4 py-4 items-end">
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <label className={portalDs.label}>Name</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Jane Smith"
          className={portalDs.input}
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <label className={portalDs.label}>Email</label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="e.g. billing@partner.com"
          className={portalDs.input}
        />
      </div>
      {!isNew && isRepDirty && (
        <PortalDsButton
          type="button"
          variant="secondary"
          size="sm"
          className="shrink-0"
          disabled={isUpdatingReferral}
          onClick={handleSaveReferralFields}
          title="Save name and email"
        >
          <Check className="h-4 w-4 mr-1.5" />
          Save
        </PortalDsButton>
      )}
    </div>
  );

  const renderReferralLinesTable = () => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>Customer lines</Label>
        <PortalDsButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={addLine}
        >
          + Add line
        </PortalDsButton>
      </div>
      <div className={portalDs.tableContainer}>
        <Table>
          <TableHeader>
            <TableRow className={portalDs.tableHead}>
              <TableHead className={cn(portalDs.tableHeadCell, "w-8")} />
              <TableHead className={cn(portalDs.tableHeadCell, "min-w-[180px]")}>Company</TableHead>
              <TableHead className={cn(portalDs.tableHeadCell, "w-24")}>Rate (%)</TableHead>
              <TableHead className={cn(portalDs.tableHeadCell, "w-28")}>Month*</TableHead>
              <TableHead className={cn(portalDs.tableHeadCell, "w-24")}>Year*</TableHead>
              <TableHead className={cn(portalDs.tableHeadCell, "w-24")}>Recurring</TableHead>
              <TableHead className={cn(portalDs.tableHeadCell, "w-20 text-right")}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((row) => {
              const isExpanded = expandedLineId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <TableRow
                    className={cn(
                      portalDs.tableBodyRow,
                      !isNew && row.companyId.trim() && portalDs.tableBodyRowInteractive,
                      isExpanded && "bg-[#F0F1F5]"
                    )}
                    onClick={(e) => {
                      if (!isNew && row.companyId.trim() && !(e.target as HTMLElement).closest("button, [role='menuitem'], input, [data-no-expand]")) {
                        toggleExpanded(row.id);
                      }
                    }}
                  >
                    <TableCell className={cn(portalDs.tableCell, "w-8 py-2")} onClick={(e) => e.stopPropagation()}>
                      {!isNew && row.companyId.trim() ? (
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-[#F0F1F5]"
                          onClick={() => toggleExpanded(row.id)}
                          aria-label={isExpanded ? "Collapse" : "Expand"}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      ) : null}
                    </TableCell>
                    <TableCell className={cn(portalDs.tableCell, "py-2")} data-no-expand>
                      <DropdownMenu
                        modal={false}
                        onOpenChange={(isOpen) => {
                          if (isOpen) setCompanyMenuSearch("");
                        }}
                      >
                        <DropdownMenuTrigger asChild>
                          <PortalDsButton
                            variant="secondary"
                            size="sm"
                            className="w-full justify-between font-normal text-left min-w-[160px]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="truncate">
                              {getCompanyName(row.companyId) || "Select company"}
                            </span>
                            <ChevronDown className="h-4 w-4 shrink-0 ml-1 opacity-50" />
                          </PortalDsButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-h-[320px] flex flex-col overflow-hidden p-0"
                          onCloseAutoFocus={(e) => e.preventDefault()}
                        >
                          <div className="shrink-0 border-b border-[#EDEEF2] bg-popover p-2">
                            <Input
                              type="search"
                              placeholder="Search companies..."
                              value={companyMenuSearch}
                              onChange={(e) => setCompanyMenuSearch(e.target.value)}
                              className={cn(portalDs.input, "h-8 text-sm")}
                              autoComplete="off"
                              onClick={(e) => e.stopPropagation()}
                              onPointerDown={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="max-h-[240px] overflow-y-auto p-1">
                            {filteredCompanyOptions.length === 0 ? (
                              <div className={cn(portalDs.hint, "px-2 py-3")}>
                                No matching companies
                              </div>
                            ) : (
                              filteredCompanyOptions.map((opt) => (
                                <DropdownMenuItem
                                  key={opt.value}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateLine(row.id, { companyId: opt.value });
                                  }}
                                >
                                  {opt.label || opt.value}
                                </DropdownMenuItem>
                              ))
                            )}
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell className={cn(portalDs.tableCell, "py-2")} data-no-expand>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        value={row.rate}
                        onChange={(e) => updateLine(row.id, { rate: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        className={cn(portalDs.input, "h-8 w-20 text-center")}
                      />
                    </TableCell>
                    <TableCell className={cn(portalDs.tableCell, "py-2")} data-no-expand>
                      <Input
                        type="number"
                        min={1}
                        max={12}
                        placeholder="—"
                        value={row.month || ""}
                        onChange={(e) => updateLine(row.id, { month: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        className={cn(portalDs.input, "h-8 w-16 text-center")}
                        disabled={row.isRecurring}
                      />
                    </TableCell>
                    <TableCell className={cn(portalDs.tableCell, "py-2")} data-no-expand>
                      <Input
                        type="number"
                        min={2020}
                        max={2030}
                        placeholder="—"
                        value={row.year || ""}
                        onChange={(e) => updateLine(row.id, { year: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        className={cn(portalDs.input, "h-8 w-16 text-center")}
                        disabled={row.isRecurring}
                      />
                    </TableCell>
                    <TableCell className={cn(portalDs.tableCell, "py-2")} data-no-expand onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        value={row.isRecurring}
                        onChange={(checked) => updateLine(row.id, { isRecurring: checked })}
                        label="Recurring"
                      />
                    </TableCell>
                    <TableCell className={cn(portalDs.tableCell, "py-2 text-right")} data-no-expand>
                      <div className="flex items-center justify-end gap-1">
                        {!isNew && (canSaveNewLine(row) || isLineDirty(row)) && (
                          <PortalDsButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 shrink-0 p-0"
                            disabled={isCreatingLine || isUpdatingLine}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveLine(row);
                            }}
                            title={row.apiId ? "Save line changes" : "Save new line"}
                          >
                            <Check className="h-4 w-4 text-[#059669]" />
                          </PortalDsButton>
                        )}
                        <PortalDsButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 shrink-0 p-0 text-[#DC2626] hover:text-[#DC2626]"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLine(row.id);
                          }}
                          title="Remove line"
                        >
                          <Trash2 className="h-4 w-4" />
                        </PortalDsButton>
                      </div>
                    </TableCell>
                  </TableRow>
                  {!isNew && isExpanded && (
                    <TableRow>
                      <TableCell colSpan={7} className="p-0 bg-white border-t border-[#EDEEF2]">
                        <div className="px-3 py-3">
                          <Label className={cn(portalDs.tableCellMuted, "block mb-2")}>
                            {row.companyId.trim()
                              ? `Invoices and lines for ${getCompanyName(row.companyId) || "this company"}`
                              : "Invoices and lines"}
                          </Label>
                          {row.companyId.trim() ? (
                      (() => {
                        const companyInvoices = getInvoicesForCompany(row.companyId);
                        const rows: Array<{
                          invoice: CommissionInvoice;
                          line?: { id: string; sku: string; skuName?: string; name: string; amount: number };
                        }> = [];
                        companyInvoices.forEach((inv) => {
                          if (inv.lines?.length) {
                            inv.lines.forEach((line) =>
                              rows.push({
                                invoice: inv,
                                line: {
                                  id: line.id,
                                  sku: line.sku ?? "—",
                                  skuName: line.skuName,
                                  name: line.name ?? "—",
                                  amount: line.amount,
                                },
                              })
                            );
                          } else {
                            rows.push({ invoice: inv });
                          }
                        });
                        return rows.length > 0 ? (
                          <div className={portalDs.tableContainer}>
                            <Table>
                              <TableHeader>
                                <TableRow className={portalDs.tableHead}>
                                  <TableHead className={portalDs.tableHeadCell}>#</TableHead>
                                  <TableHead className={portalDs.tableHeadCell}>Date</TableHead>
                                  <TableHead className={portalDs.tableHeadCell}>Status</TableHead>
                                  <TableHead className={portalDs.tableHeadCell}>SKU</TableHead>
                                  <TableHead className={cn(portalDs.tableHeadCell, "max-w-[10rem] w-[10rem]")}>Line item</TableHead>
                                  <TableHead className={cn(portalDs.tableHeadCell, "w-28 min-w-[7rem]")}>Rate (%)</TableHead>
                                  <TableHead className={cn(portalDs.tableHeadCell, "text-right")}>Amount</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {rows.map((r, idx) => (
                                  <TableRow key={r.line ? `${r.invoice.id}-${r.line.id}` : `${r.invoice.id}-${idx}`} className={portalDs.tableBodyRow}>
                                    <TableCell className={portalDs.tableCell}>{r.invoice.invoiceNumber || "—"}</TableCell>
                                    <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                                      {r.invoice.status === "paid" && r.invoice.paidDate ? r.invoice.paidDate : "—"}
                                    </TableCell>
                                    <TableCell className={cn(portalDs.tableCell, "capitalize")}>{r.invoice.status ?? "—"}</TableCell>
                                    <TableCell className={portalDs.tableCell}>{r.line?.skuName ?? r.line?.sku ?? "—"}</TableCell>
                                    <TableCell className={cn(portalDs.tableCell, "max-w-[10rem] w-[10rem] truncate")} title={r.line?.name ?? undefined}>{r.line?.name ?? "—"}</TableCell>
                                    <TableCell className={cn(portalDs.tableCell, "min-w-[7rem]")}>
                                      {r.line != null ? (
                                        <BorderedInput
                                          type="number"
                                          min={0}
                                          max={100}
                                          step={0.5}
                                          value={getOrInitLineItemOverride(row.id, r.line.id, row.rate).rate}
                                          onChange={(e) =>
                                            updateLineItemOverride(row.id, r.line!.id, { rate: e.target.value })
                                          }
                                          placeholder={row.rate || "0"}
                                          className="w-full min-w-[6rem] max-w-[8rem]"
                                        />
                                      ) : (
                                        "—"
                                      )}
                                    </TableCell>
                                    <TableCell className={cn(portalDs.tableCell, "text-right")}>
                                      {r.line != null ? formatCurrency(r.line.amount) : "—"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <p className={cn(portalDs.hint, "py-2")}>No invoices found for this company.</p>
                        );
                      })()
                    ) : (
                      <p className={cn(portalDs.hint, "py-2")}>Select a company to view invoices.</p>
                    )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
                );
              })}
            {lines.length === 0 && (
              <CommissionTableEmptyState
                colSpan={7}
                title="No lines yet"
                description="Add a line to associate a customer with a rate and recurring option."
              />
            )}
          </TableBody>
        </Table>
        <p className={cn(portalDs.caption, "mt-2 px-0")}>
          * Month and year are only used for one-off referrals.
        </p>
      </div>
    </div>
  );

  return (
    <>
    <Sheet
      open={open}
      onOpenChange={handleOpenChange}
    >
      <SheetContent className="!w-1/2 !max-w-none overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle className={portalDs.sheetTitle}>
            {isNew ? "Add external rep" : "Edit external rep"}
          </SheetTitle>
        </SheetHeader>
        {renderNameEmailRow()}
        {renderPlanTemplateRow()}
        {!isNew && renderAssignedCustomers()}
        {!isNew && rep?.id && (
          <div className={cn(portalDs.formPanel, "mb-4 p-0 overflow-hidden")}>
            <PortalDsButton
              type="button"
              variant="secondary"
              size="sm"
              className="flex items-center gap-2 w-full rounded-md justify-start"
              onClick={() => setBreakdownExpanded((p) => !p)}
            >
              {breakdownExpanded ? (
                <ChevronUp className="h-4 w-4 shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0" />
              )}
              <span>Month-by-month commission breakdown</span>
              {referralBreakdownRows.length > 0 && (
                <span className={cn(portalDs.tableCellMuted, "font-normal")}>
                  ({referralBreakdownRows.length} month{referralBreakdownRows.length !== 1 ? "s" : ""})
                </span>
              )}
            </PortalDsButton>
            {breakdownExpanded && (
              <div className="overflow-hidden border-t border-[#EDEEF2]">
                <div className={portalDs.tableContainer}>
                  <Table>
                    <TableHeader>
                      <TableRow className={portalDs.tableHead}>
                        <TableHead className={portalDs.tableHeadCell}>Month</TableHead>
                        <TableHead className={portalDs.tableHeadCell}>Year</TableHead>
                        <TableHead className={cn(portalDs.tableHeadCell, "text-right w-1/4 min-w-[25%]")}>Commission</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {referralBreakdownRows.length === 0 ? (
                        <CommissionTableEmptyState
                          colSpan={3}
                          title="No commission data yet"
                          description="Monthly commission rows will appear here once reports are available for this rep."
                        />
                      ) : (
                        referralBreakdownRows.map((row) => {
                          const rowKey = row.id ?? `report-${row.year}-${row.month}`;
                          const displayValue = editingCommissionByReportId[rowKey] ?? String(Number((row.commission ?? 0).toFixed(2)));
                          const handleCommissionBlur = () => {
                            if (!row.id) return;
                            const parsed = parseFloat(displayValue);
                            if (!Number.isNaN(parsed) && parsed !== (row.commission ?? 0)) {
                              updateCommissionReport({ id: row.id, commission: parsed }).unwrap().catch(() => {});
                            }
                            setEditingCommissionByReportId((prev) => {
                              const next = { ...prev };
                              delete next[rowKey];
                              return next;
                            });
                          };
                          return (
                            <TableRow key={rowKey} className={portalDs.tableBodyRow}>
                              <TableCell className={portalDs.tableCell}>
                                {MONTH_OPTIONS.find((m) => m.value === String(row.month))?.label ?? row.month}
                              </TableCell>
                              <TableCell className={portalDs.tableCell}>{row.year}</TableCell>
                              <TableCell className={cn(portalDs.tableCell, "text-right font-medium w-1/4 min-w-[25%]")}>
                                {row.id ? (
                                  <Input
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    value={displayValue}
                                    onChange={(e) =>
                                      setEditingCommissionByReportId((prev) => ({ ...prev, [rowKey]: e.target.value }))
                                    }
                                    onBlur={handleCommissionBlur}
                                    className={cn(portalDs.input, "h-8 w-full min-w-[6rem] max-w-[10rem] text-right ml-auto")}
                                  />
                                ) : (
                                  formatCurrency(Number((row.commission ?? 0).toFixed(2)))
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
        {renderReferralLinesTable()}
        <SheetFooter>
          {isNew && (
            <PortalDsButton type="button" variant="primary" size="sm" onClick={handleSave}>
              Save
            </PortalDsButton>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
    <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
      <DialogContent className="sm:max-w-md bg-white" onPointerDownOutside={e => e.preventDefault()}>
        <DialogTitle className={portalDs.sectionTitle}>Unsaved changes</DialogTitle>
        <p className={portalDs.sheetDescription}>
          You have unsaved changes. Are you sure you want to close? Your changes will be lost.
        </p>
        <DialogFooter className="gap-2 sm:gap-0">
          <PortalDsButton variant="secondary" size="sm" onClick={() => setShowDiscardConfirm(false)}>
            Stay
          </PortalDsButton>
          <PortalDsButton variant="primary" size="sm" onClick={handleConfirmDiscard}>
            Discard changes
          </PortalDsButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ExternalRepDrawer;
